import React, { Suspense, useEffect, useState } from "react"
import { LoadingSpinner } from "@kit/design/loading-spinner"
import { useCurrentUser } from "hooks/use-current-user"
import { useSubscriberProfileId } from "./use-subscriber-profile-id"

const ProfileDrawerPanel = React.lazy(() =>
  import("./components/profile-drawer-panel").then(m => ({
    default: m.ProfileDrawerPanel
  }))
)

// Covers the one-time delay while the drawer chunk downloads, so the first open doesn't look like a no-op.
const PANEL_LOADING_FALLBACK = (
  <div className="fixed inset-0 z-[51] flex items-center justify-center bg-black/70">
    <span role="status" aria-label="Loading subscriber profile">
      <LoadingSpinner className="w-10 h-10 text-white" />
    </span>
  </div>
)

export function SubscriberProfileDrawer() {
  const { anonymous } = useCurrentUser()
  const subscriberProfileId = useSubscriberProfileId()
  const [hasOpenedOnce, setHasOpenedOnce] = useState(false)

  // Latches true on first open and never resets, so later closes don't unmount the panel.
  useEffect(() => {
    if (subscriberProfileId) {
      setHasOpenedOnce(true)
    }
  }, [subscriberProfileId])

  // A `subscriber_profile_id` hash can reach a logged-out page — e.g. the browser preserves the
  // original URL's fragment across the redirect to /users/login when an unauthenticated request
  // hits an authenticated page. Without this, the drawer would open (and fetch a subscriber) on
  // that login page despite there being no session to fetch it with.
  if (anonymous || !hasOpenedOnce) {
    return null
  }

  // Stays mounted after the first open so ProfileDrawerPanel's own close-transition can play instead of being unmounted mid-animation.
  return (
    <Suspense fallback={PANEL_LOADING_FALLBACK}>
      <ProfileDrawerPanel subscriberProfileId={subscriberProfileId} />
    </Suspense>
  )
}
